import React, { useState } from 'react';
import buildStyle from '../build-path.module.scss';
import './text.scss';
import { IonButton, IonCol, IonInput, IonRow, IonTextarea } from '@ionic/react';
import BuildGenerateCrate from '../../build-generate-crate/build-generate-crate';
import { iTrack } from '../../../../types/ITrack';
import { GridReadyEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { iCrate } from '../../../../types/ICrate';
import { useHistory } from 'react-router-dom';
import { useDispatch } from '../../../../store/hooks';

export default function TextPath() {
  const theme = useSelector((state: RootState) => state.themeState.theme);
  const [crateName, setCrateName] = useState('');
  const [tracks, setTracks] = useState<iTrack[]>([]);
  const [chosenDelimiter, setChosenDelimiter] = useState<string>('');
  const [gridApi, setGridApi] = useState<any>();
  const [showDelimiterChange, setShowDelimiterChange] = useState(false);
  const [pasteContent, setPasteContent] = useState('');

  const history = useHistory();
  const dispatch = useDispatch();

  const delimiters: { [key: string]: string } = {
    Comma: ',',
    Hyphen: '-',
    Tab: '\t',
    Pipe: '|',
    Semicolon: ';',
    Colon: ':',
    Slash: '/',
  };

  const buildCrate = () => {
    const newCrate: iCrate = {
      _id: '',
      Title: crateName,
      Description: 'Created from Crate Hackers Crate Builder (text import)',
      Tracks: tracks,
      createdFrom: 'Text',
    };

    return newCrate;
  };

  // normalize fancy dashes to standard hyphen
  const normalizeLine = (line: string): string =>
    line.replace(/[\u2013\u2014\u2015]/g, '-');

  // split a string once on first occurrence of delim
  const splitOnce = (line: string, delim: string): string[] => {
    const idx = line.indexOf(delim);
    if (idx >= 0) {
      return [line.slice(0, idx).trim(), line.slice(idx + delim.length).trim()];
    }
    return [line.trim()];
  };

  // detect best delimiter for a given line
  const detectDelimiterForLine = (rawLine: string): string => {
    const line = normalizeLine(rawLine);
    // build list of [delimiter, fieldCount]
    const candidates = Object.values(delimiters).map((d) => ({
      delim: d,
      count: line.split(d).length,
      index: line.indexOf(d),
    }));
    // filter only those that actually split
    const multi = candidates.filter((c) => c.count > 1 && c.index >= 0);
    if (multi.length === 0) return '';
    // prefer split into exactly 2 fields
    const twos = multi.filter((c) => c.count === 2);
    if (twos.length > 0) {
      // pick the one appearing earliest in the line
      return twos.sort((a, b) => a.index - b.index)[0].delim;
    }
    // else pick the one with smallest count (fewest splits)
    const best = multi.sort((a, b) => a.count - b.count)[0];
    return best.delim;
  };

  // parse a single line into an iTrack
  const parseTrack = (rawLine: string): iTrack => {
    const line = normalizeLine(rawLine);
    const delim = detectDelimiterForLine(rawLine);
    const parts = delim ? splitOnce(line, delim) : [line.trim()];
    if (parts.length > 1) {
      return { ID: '', Artist: parts[0], Title: parts[1] };
    }
    return { ID: '', Artist: '', Title: parts[0] };
  };

  // parse all lines of pasted content
  const parseContent = (content: string) => {
    const lines = content
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l);

    setTracks(lines.map((line) => parseTrack(line)));
  };

  const handlePasteContent = (e: any) => {
    const val = e.detail.value || '';
    setPasteContent(val);
    parseContent(val);
  };

  const flipArtistTitle = () => {
    const flippedTracks = tracks.map((track) => ({
      ID: '',
      Artist: track.Title,
      Title: track.Artist,
    }));
    setTracks(flippedTracks);
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
  };

  const [columnDefs] = useState([
    { field: 'id', hide: true },
    {
      field: 'Artist',
      headerName: 'Artist',
      flex: 1,
      resizable: true,
      filter: false,
      sortable: false,
      editable: false,
      draggable: false,
      lockPosition: true,
      suppressMenu: true,
    },
    {
      field: 'Title',
      headerName: 'Title',
      flex: 1,
      resizable: true,
      filter: false,
      sortable: false,
      editable: false,
      draggable: false,
      lockPosition: true,
      suppressMenu: true,
    },
  ]);

  return (
    <>
      <IonRow>
        <IonCol size="12">
          <div className={buildStyle.buildHeader}>
            <div>What should we name your crate?</div>
          </div>
          <IonInput
            className={buildStyle['build-input']}
            placeholder="Crate Name"
            labelPlacement="floating"
            fill="outline"
            value={crateName}
            onIonInput={(e: any) => setCrateName(e.target.value)}
          >
            <div slot="label">Crate Name</div>
          </IonInput>
        </IonCol>
      </IonRow>

      <IonRow>
        <IonCol size="12">
          <IonTextarea
            label="Paste text here"
            labelPlacement="floating"
            fill="outline"
            className={buildStyle['build-input']}
            autoGrow={true}
            rows={8}
            value={pasteContent}
            onIonInput={handlePasteContent}
          />
        </IonCol>
      </IonRow>

      {/*<IonAlert*/}
      {/*  cssClass={'delimiterAlert'}*/}
      {/*  isOpen={showDelimiterChange}*/}
      {/*  onDidDismiss={() => setShowDelimiterChange(false)}*/}
      {/*  header="Change Delimiter"*/}
      {/*  message={`Current delimiter: ${chosenDelimiter} (${Object.keys(*/}
      {/*    delimiters,*/}
      {/*  ).find((key) => delimiters[key] === chosenDelimiter)})`}*/}
      {/*  buttons={Object.keys(delimiters).map((key) => ({*/}
      {/*    text: `${key}`,*/}
      {/*    handler: () => handleDelimiterChange(delimiters[key]),*/}
      {/*  }))}*/}
      {/*/>*/}

      {pasteContent && (
        <>
          <IonRow style={{ padding: '20px' }}>
            <IonCol size="12" sizeMd="6">
              {/*<IonText color="primary">*/}
              {/*  {chosenDelimiter ? (*/}
              {/*    <h3>*/}
              {/*      Detected Delimiter:{' '}*/}
              {/*      {Object.keys(delimiters).find(*/}
              {/*        (key) => delimiters[key] === chosenDelimiter,*/}
              {/*      )}*/}
              {/*    </h3>*/}
              {/*  ) : (*/}
              {/*    <h3>*/}
              {/*      No delimiter detected—using each line as Title/Artist only*/}
              {/*    </h3>*/}
              {/*  )}*/}
              {/*</IonText>*/}
            </IonCol>
            <IonCol>
              {/*{chosenDelimiter && (*/}
              {/*  <IonButton onClick={() => setShowDelimiterChange(true)}>*/}
              {/*    Change Delimiter*/}
              {/*  </IonButton>*/}
              {/*)}*/}
              {/*TODO: Add this back in later to get it working */}
            </IonCol>
            <IonCol>
              {tracks.length > 0 && (
                <IonButton
                  className="text-primary-buttons"
                  onClick={flipArtistTitle}
                >
                  Flip Artist and Title
                </IonButton>
              )}
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol size="12">
              <div
                className="ag-theme-alpine"
                style={{ height: 344, width: '100%' }}
              >
                {tracks.length && (
                  <AgGridReact
                    className={`ag-theme-alpine${theme !== 'light' ? '-dark' : ''}`}
                    rowData={tracks}
                    columnDefs={columnDefs}
                    onGridReady={onGridReady}
                  />
                )}
              </div>
            </IonCol>
          </IonRow>
        </>
      )}

      <BuildGenerateCrate
        buildCrateFn={buildCrate}
        isBtnDisabled={crateName === '' || tracks.length === 0}
      />
    </>
  );
}
